/**
 * useAccountPickerState — les gestes de « Qui utilise Masteur ? » : ouvrir un compte, ouvrir
 * l'espace parent d'un groupe, retirer un compte ou une famille de l'appareil.
 *
 * Port de `FamilyAccountSelectionScreen` (legacy), geste par geste :
 *  - **Ouvrir un compte** (`handleAccountPress`) : un lien en attente qui vise l'espace parent est
 *    oublié ; avec un jeton de notifications, `unsubscribeFromNotifications` part d'abord, puis
 *    `validateToken` sur son succès COMME sur son échec ; sans lui, `validateToken` directement.
 *  - **Espace parent** (`handleParentModePress`) : un lien en attente qui ne vise PAS l'espace parent
 *    est oublié ; `setParentModeFamilyId` (`-1` hors famille) ; `validateToken` par le premier élève du
 *    groupe dans l'ordre de l'appareil ; pas de désinscription des notifications.
 *  - `validateToken` part avec le jeton du compte en 6ᵉ argument d'`apiRequest`, comme le legacy.
 *    Réussi : 500 ms d'attente (contre le scintillement), `setIsParentMode(true)` si c'est l'espace
 *    parent, puis `switchAccount`. Un 401 : `apiRequest` a déjà déconnecté (`LOGOUT_401`), l'écran
 *    pousse `login` avec l'adresse du compte. Toute autre erreur : UNE ligne sous les tuiles
 *    (Wajih, 19/10) à la place de la boîte « Erreur » du legacy.
 *  - **L'arrivée par un lien** (`useAutoBypassDeepLink`, monté aussi par le legacy) : un lien qui
 *    désigne un seul compte l'ouvre sans geste, par la même porte que les deux autres.
 *  - **Retirer** (`onDeleteAccount` / `onDeleteFamily`) : `revokeSanctumToken` avec le jeton de CE
 *    compte, puis `removeAuthToken`. Une famille se révoque compte par compte, chacun avec SON
 *    jeton, l'un après l'autre (Wajih, 19/10) — le legacy lançait tout sans attendre.
 *
 * Écrit à la main plutôt que par `useApiMutation`, pour deux gardes de plus : le jeton du compte en
 * 6ᵉ argument d'`apiRequest` (que `useApiMutation` ne transmet pas), et l'enchaînement de deux
 * appels (désinscription → validation) et d'une file (les révocations d'une famille).
 *
 * Le succès d'une révocation remet à zéro la session, le compte courant et le mode parent dans les
 * réducteurs : sans effet ici, l'écran n'est monté que déconnecté (la pile d'avant connexion, où
 * « Changer de compte » ramène après la déconnexion). Mais une révocation qui répondrait APRÈS
 * l'ouverture d'un compte le déconnecterait : aucune ouverture ne part tant qu'une révocation est en vol.
 */
import { useCallback, useEffect, useReducer, useRef } from "react"
import { useNavigation } from "@react-navigation/native"
import { useDispatch, useSelector } from "react-redux"

import { pendingDeepLinkIsParentPath } from "../../../../../../data/auth/pendingDeepLinkMode"
import { useAutoBypassDeepLink } from "../../../../../../data/auth/useAutoBypassDeepLink"
import { apiIdle, apiRequest } from "../../../../../../data/redux/actions/api"
import { removeAuthToken, switchAccount } from "../../../../../../data/redux/actions/authTokens"
import { clearPendingDeepLink } from "../../../../../../data/redux/actions/notifications"
import { setIsParentMode, setParentModeFamilyId } from "../../../../../../data/redux/actions/parentMode"
import { authService } from "../../../../../../data/services/authService"
import { type ApiStoredAccount } from "../../../../adapters/auth/AccountPicker/storedAccountAdapter"
import {
    type UIAccountPickerRemoval,
    type UIAccountPickerTarget,
    type UIStoredAccount,
    type UIStoredAccountFamily,
    type UIStoredAccountGroup,
} from "../../../types"

/** La ligne d'une ouverture manquée (Wajih, 19/10). */
export const ACCOUNT_OPEN_FAILED = "Ce compte n'a pas pu s'ouvrir. Réessayez dans un instant."

/** L'attente du legacy entre la validation et la bascule, contre le scintillement. */
export const SWITCH_DELAY_MS = 500

// ── L'état d'interface : un réducteur pur ──

export type AccountPickerUi = {
    opening: UIAccountPickerTarget | null
    managing: boolean
    removal: UIAccountPickerRemoval | null
    error: string | null
}

export const INITIAL_ACCOUNT_PICKER_UI: AccountPickerUi = { opening: null, managing: false, removal: null, error: null }

export type AccountPickerUiAction =
    | { type: "OPEN"; target: UIAccountPickerTarget }
    | { type: "OPENED" }
    | { type: "OPEN_FAILED"; error: string | null }
    | { type: "TOGGLE_MANAGING" }
    | { type: "ASK_REMOVAL"; removal: UIAccountPickerRemoval }
    | { type: "CLOSE_REMOVAL" }

export function accountPickerUiReducer(state: AccountPickerUi, action: AccountPickerUiAction): AccountPickerUi {
    switch (action.type) {
        case "OPEN":
            return { ...state, opening: action.target, error: null }
        case "OPENED":
            return { ...state, opening: null }
        case "OPEN_FAILED":
            return { ...state, opening: null, error: action.error }
        case "TOGGLE_MANAGING":
            return { ...state, managing: !state.managing, removal: null, error: null }
        case "ASK_REMOVAL":
            return { ...state, removal: action.removal }
        case "CLOSE_REMOVAL":
            return { ...state, removal: null }
    }
}

/** Les comptes qu'un retrait confirmé révoque, dans l'ordre de l'écran. */
export function removalAccountIds(removal: UIAccountPickerRemoval): (string | number)[] {
    if (removal.kind === "account") return [removal.account.id]
    return [...removal.family.students, ...removal.family.others].map((account) => account.id)
}

// ── Les gestes ──

type ApiSlice = { request?: boolean; success?: boolean; error?: { status?: number } | null }

/** Ce qui s'ouvre : le compte stocké (jeton compris) et l'entrée dans l'espace parent. */
type PendingOpen = { account: ApiStoredAccount; parentSpace: boolean }

/** Ce que `resolveAutoBypassTarget` rend (`data/utility/familyUtils`). */
type AutoBypassTarget = { token: ApiStoredAccount; isParentMode: boolean; familyId: string | number | null }

export type AccountPickerState = AccountPickerUi & {
    onOpenAccount: (account: UIStoredAccount) => void
    onOpenParentSpace: (group: UIStoredAccountGroup) => void
    onAddAccount: () => void
    onToggleManaging: () => void
    onRemoveAccount: (account: UIStoredAccount) => void
    onRemoveFamily: (family: UIStoredAccountFamily) => void
    onConfirmRemoval: (removal: UIAccountPickerRemoval) => void
    onCancelRemoval: () => void
}

export function useAccountPickerState(): AccountPickerState {
    const dispatch = useDispatch()
    const navigation = useNavigation<{
        navigate: (name: string) => void
        push: (name: string, params?: Record<string, unknown>) => void
    }>()
    const [ui, dispatchUi] = useReducer(accountPickerUiReducer, INITIAL_ACCOUNT_PICKER_UI)

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const storedTokens = useSelector((state: any) => state?.data?.authTokens?.storedTokens) as
        ApiStoredAccount[] | undefined
    const expoToken = useSelector((state: any) => state?.data?.session?.object?.expo_token) as string | undefined
    const pendingDeepLink = useSelector((state: any) => state?.notifications?.pendingDeepLink) as string | undefined
    const unsubscribeReq = useSelector((state: any) => state?.api?.unsubscribeFromNotifications) as ApiSlice | undefined
    const validateReq = useSelector((state: any) => state?.api?.validateToken) as ApiSlice | undefined
    const revokeReq = useSelector((state: any) => state?.api?.revokeSanctumToken) as ApiSlice | undefined
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const pendingOpenRef = useRef<PendingOpen | null>(null)
    const awaitingUnsubscribeRef = useRef(false)
    const awaitingValidateRef = useRef(false)
    const revokeQueueRef = useRef<ApiStoredAccount[]>([])
    const awaitingRevokeRef = useRef(false)

    const findAccount = (id: string | number) => storedTokens?.find((account) => account?.userId === id) ?? null

    const validate = useCallback(
        (token: string | number | undefined) => {
            awaitingValidateRef.current = true
            dispatch(apiRequest(authService.validateToken, null, null, null, null, token || -1) as never)
        },
        [dispatch]
    )

    /** Une ouverture à la fois, et aucune pendant une révocation (voir l'en-tête). */
    const canOpen = () => pendingOpenRef.current == null && !awaitingRevokeRef.current

    const onOpenAccount = (shown: UIStoredAccount) => {
        const account = findAccount(shown.id)
        if (account == null || !canOpen()) return
        if (pendingDeepLinkIsParentPath(pendingDeepLink) === true) dispatch(clearPendingDeepLink())

        pendingOpenRef.current = { account, parentSpace: false }
        dispatchUi({ type: "OPEN", target: { kind: "account", accountId: shown.id } })
        if (expoToken) {
            awaitingUnsubscribeRef.current = true
            dispatch(apiRequest(authService.unsubscribeFromNotifications, {}, { expo_token: expoToken }) as never)
        } else {
            validate(account.token)
        }
    }

    const onOpenParentSpace = (group: UIStoredAccountGroup) => {
        if (!canOpen()) return
        if (pendingDeepLinkIsParentPath(pendingDeepLink) === false) dispatch(clearPendingDeepLink())

        const account = group.parentSpaceAccountId != null ? findAccount(group.parentSpaceAccountId) : null
        // Sans élève dans le groupe, la tuile n'existe pas : le legacy s'arrêtait là aussi.
        if (account == null) return

        pendingOpenRef.current = { account, parentSpace: true }
        dispatchUi({ type: "OPEN", target: { kind: "parentSpace", groupId: group.id } })
        dispatch(setParentModeFamilyId(group.kind === "individual" ? -1 : group.id))
        validate(account.token)
    }

    useAutoBypassDeepLink((target: AutoBypassTarget) => {
        pendingOpenRef.current = { account: target.token, parentSpace: target.isParentMode }
        if (target.isParentMode) {
            if (target.familyId !== null) dispatch(setParentModeFamilyId(target.familyId))
            dispatchUi({ type: "OPEN", target: { kind: "parentSpace", groupId: target.familyId ?? -1 } })
        } else if (target.token.userId != null) {
            dispatchUi({ type: "OPEN", target: { kind: "account", accountId: target.token.userId } })
        }
        validate(target.token.token)
    })

    // La désinscription, réussie ou non, mène à la validation.
    useEffect(() => {
        if (!awaitingUnsubscribeRef.current || !(unsubscribeReq?.success || unsubscribeReq?.error)) return
        awaitingUnsubscribeRef.current = false
        dispatch(apiIdle(authService.unsubscribeFromNotifications) as never)
        const account = pendingOpenRef.current?.account
        if (account) validate(account.token)
    }, [dispatch, validate, unsubscribeReq?.success, unsubscribeReq?.error])

    useEffect(() => {
        if (!awaitingValidateRef.current || !validateReq?.success) return
        awaitingValidateRef.current = false
        dispatch(apiIdle(authService.validateToken) as never)
        const opened = pendingOpenRef.current
        setTimeout(() => {
            if (opened?.parentSpace) dispatch(setIsParentMode(true))
            dispatch(switchAccount(opened?.account))
            pendingOpenRef.current = null
            dispatchUi({ type: "OPENED" })
        }, SWITCH_DELAY_MS)
    }, [dispatch, validateReq?.success])

    useEffect(() => {
        if (!awaitingValidateRef.current || !validateReq?.error) return
        awaitingValidateRef.current = false
        const opened = pendingOpenRef.current
        pendingOpenRef.current = null
        dispatch(apiIdle(authService.validateToken) as never)
        if (validateReq.error.status === 401) {
            dispatchUi({ type: "OPEN_FAILED", error: null })
            navigation.push("login", { prefillEmail: opened?.account.email })
        } else {
            dispatchUi({ type: "OPEN_FAILED", error: ACCOUNT_OPEN_FAILED })
        }
    }, [dispatch, navigation, validateReq?.error])

    // ── Le retrait : une file, un compte à la fois, chacun avec son jeton ──

    const revokeNext = useCallback(() => {
        const next = revokeQueueRef.current.shift()
        if (next == null) {
            awaitingRevokeRef.current = false
            return
        }
        awaitingRevokeRef.current = true
        dispatch(apiRequest(authService.revokeSanctumToken, null, null, null, null, next.token) as never)
    }, [dispatch])

    useEffect(() => {
        if (!awaitingRevokeRef.current || !(revokeReq?.success || revokeReq?.error)) return
        // Révoqué ou non, le compte a déjà quitté l'appareil (le legacy le retirait sans attendre).
        dispatch(apiIdle(authService.revokeSanctumToken) as never)
        revokeNext()
    }, [dispatch, revokeNext, revokeReq?.success, revokeReq?.error])

    const onConfirmRemoval = (removal: UIAccountPickerRemoval) => {
        dispatchUi({ type: "CLOSE_REMOVAL" })
        const accounts = removalAccountIds(removal)
            .map(findAccount)
            .filter((account): account is ApiStoredAccount => account != null)
        // Le compte quitte l'écran tout de suite, comme dans le legacy ; sa révocation suit, dans la file.
        accounts.forEach((account) => dispatch(removeAuthToken(account.userId)))
        revokeQueueRef.current.push(...accounts)
        if (!awaitingRevokeRef.current) revokeNext()
    }

    // Un reste sur une tranche se rejouerait au prochain montage de l'écran. On ne rend au repos que
    // ce qu'on attend encore : `validateToken` sert aussi l'arrivée par un lien d'`AuthNavigator`.
    useEffect(
        () => () => {
            if (awaitingUnsubscribeRef.current) dispatch(apiIdle(authService.unsubscribeFromNotifications) as never)
            if (awaitingValidateRef.current) dispatch(apiIdle(authService.validateToken) as never)
            if (awaitingRevokeRef.current) dispatch(apiIdle(authService.revokeSanctumToken) as never)
        },
        [dispatch]
    )

    return {
        ...ui,
        onOpenAccount,
        onOpenParentSpace,
        onAddAccount: () => navigation.navigate("account-choice"),
        onToggleManaging: () => dispatchUi({ type: "TOGGLE_MANAGING" }),
        onRemoveAccount: (account) => dispatchUi({ type: "ASK_REMOVAL", removal: { kind: "account", account } }),
        onRemoveFamily: (family) => dispatchUi({ type: "ASK_REMOVAL", removal: { kind: "family", family } }),
        onConfirmRemoval,
        onCancelRemoval: () => dispatchUi({ type: "CLOSE_REMOVAL" }),
    }
}
